import React, { useState, useEffect } from "react";
import { Box, Typography, Card } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import {
  Groups,
  Nature,
  Diversity3,
  Bolt,
  Autorenew,
  Public,
} from "@mui/icons-material";

// Animation keyframes
const floatBalloon = keyframes`
  0%, 100% { 
    transform: translateY(0px) translateX(0px) rotate(0deg); 
  }
  25% { 
    transform: translateY(-15px) translateX(5px) rotate(2deg); 
  }
  50% { 
    transform: translateY(-25px) translateX(-3px) rotate(-1deg); 
  }
  75% { 
    transform: translateY(-15px) translateX(-5px) rotate(1deg); 
  }
`;

const gradientShift = keyframes`
  0%, 100% { 
    background-position: 0% 50%;
  }
  50% { 
    background-position: 100% 50%;
  }
`;

const rotateCircle = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const boxLoad = keyframes`
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.8);
  }
  70% {
    opacity: 0.8;
    transform: translateY(-5px) scale(1.02);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// Styled Components
const ModalContainer = styled(Box)({
  width: "100%",
  height: "100%",
  borderRadius: "40px",
  background:
    "linear-gradient(135deg, #33863aff 0%, #000000ff 50%, #62bd6aff 100%)",
  backgroundSize: "200% 200%",
  animation: `${gradientShift} 12s ease infinite`,
  position: "relative",
  overflow: "hidden",
  padding: "20px",
  display: "flex",
  flexDirection: "column",
});

const CenterCircle = styled(Box)({
  position: "absolute",
  top: "40%",
  left: "50%",
  transform: "translate(-50%, -50%)",

  // Desktop: 27% width, 48% height
  width: "50%",
  height: "20%",
  minWidth: "180px",
  // maxWidth: "550px",
  aspectRatio: "9 / 16",

  background:
    "linear-gradient(135deg, #4f8552ff 0%, #000000ff 50%, #3c6b3fff 100%)",
  borderRadius: "50%",
  color: "white",
  boxShadow:
    "0 15px 35px rgba(27, 94, 32, 0.4), inset 0 0 0 3px rgba(255,255,255,0.1)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: "20px",
  zIndex: 10,

  "&::before": {
    content: '""',
    position: "absolute",
    left: "-3px",
    right: "-3px",
    background:
      "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%)",
    borderRadius: "50%",
    animation: `${rotateCircle} 15s linear infinite reverse`,
    zIndex: -1,
  },
});

const MainTitle = styled(Typography)({
  fontSize: "2rem",
  fontWeight: 700,
  marginBottom: "6px",
  textShadow: "1px 1px 4px rgba(0,0,0,0.3)",
  position: "relative",
  zIndex: 2,
  lineHeight: 1.2,
});

const Subtitle = styled(Typography)({
  fontSize: "1.4rem",
  fontWeight: 500,
  opacity: 0.95,
  position: "relative",
  zIndex: 2,
  lineHeight: 1.1,
});

const BalloonsContainer = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100%", // Reduced height to make space for video
  minHeight: "380px",
  zIndex: 5,
});

const VideoContainer = styled(Box)({
  width: "100%",
  height: "350px",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.4)",
  background: "#000",
  marginTop: "20px",
  zIndex: 5,
});

const ImpactBalloon = styled(Card)(
  ({ position, delay, color, hovered, boxLoaded }) => ({
    position: "absolute",
    width: "220px",
    minHeight: "160px",
    background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
    border: `2px solid ${hovered ? color : `${color}40`}`,
    borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%",
    animation: boxLoaded
      ? `${floatBalloon} 8s ease-in-out infinite, ${boxLoad} 1.2s ease-out both`
      : "none",
    animationDelay: boxLoaded ? `${delay}s, 0s` : "0s",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: hovered
      ? `0 20px 40px ${color}25, 0 0 0 2px ${color}15`
      : `0 10px 25px rgba(0, 0, 0, 0.1), 0 5px 15px ${color}15`,
    transform: hovered ? "scale(1.05) translateY(-8px)" : "scale(1)",
    overflow: "hidden",
    zIndex: hovered ? 20 : 5,
    opacity: boxLoaded ? 1 : 0,

    "&::before": {
      content: '""',
      position: "absolute",
      bottom: "-30px",
      left: "50%",
      transform: "translateX(-50%)",
      width: "2px",
      height: "40px",
      background: `linear-gradient(to bottom, ${color}40, transparent)`,
      transition: "all 0.3s ease",
      opacity: hovered ? 0.7 : 0.4,
    },
    ...position,
  })
);

const BalloonContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  padding: "20px 15px",
  height: "70%",
  justifyContent: "center",
  position: "relative",
  zIndex: 2,
});

const IconContainer = styled(Box)(({ hovered, color, boxLoaded }) => ({
  color: "white",
  background: `linear-gradient(135deg, ${color} 0%, ${color}80 100%)`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "50px",
  height: "50px",
  borderRadius: "50%",
  fontSize: "2rem",
  boxShadow: `0 6px 20px ${color}40`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  transform: hovered ? "scale(1.1) rotate(5deg)" : "scale(1) rotate(0deg)",
  animation: boxLoaded ? `${floatBalloon} 5s ease-in-out infinite` : "none",
  opacity: boxLoaded ? 1 : 0,
}));

const BalloonTitle = styled(Typography)(({ boxLoaded }) => ({
  fontSize: "1.3rem",
  fontWeight: 600,
  marginBottom: "6px",
  lineHeight: 1.2,
  background: "linear-gradient(135deg, #ffffffff 0%, #81c784 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  opacity: boxLoaded ? 1 : 0,
  transition: "opacity 0.6s ease",
  transitionDelay: boxLoaded ? "0.3s" : "0s",
}));

const BalloonDescription = styled(Typography)(({ boxLoaded }) => ({
  fontSize: "1.2rem",
  color: "#ffffffff",
  lineHeight: 1.2,
  flex: 1,
  opacity: boxLoaded ? 1 : 0,
  transition: "opacity 0.6s ease",
  transitionDelay: boxLoaded ? "0.5s" : "0s",
}));

const Impact = () => {
  const [, setIsVideoPlaying] = useState(false);
  const [hoveredBalloon, setHoveredBalloon] = useState(null);
  const [loadedStates, setLoadedStates] = useState({
    balloon1: false,
    balloon2: false,
    balloon3: false,
    balloon4: false,
    balloon5: false,
    balloon6: false,
  });

  // Handle video end
  const handleVideoEnd = () => {
    setIsVideoPlaying(false);
  };

  useEffect(() => {
    const loadSequence = async () => {
      // Balloon 1
      await new Promise((resolve) => setTimeout(resolve, 300));
      setLoadedStates((prev) => ({ ...prev, balloon1: true }));

      // Balloon 2
      await new Promise((resolve) => setTimeout(resolve, 300));
      setLoadedStates((prev) => ({ ...prev, balloon2: true }));

      // Balloon 3
      await new Promise((resolve) => setTimeout(resolve, 300));
      setLoadedStates((prev) => ({ ...prev, balloon3: true }));

      // Balloon 4
      await new Promise((resolve) => setTimeout(resolve, 300));
      setLoadedStates((prev) => ({ ...prev, balloon4: true }));

      // Balloon 5
      await new Promise((resolve) => setTimeout(resolve, 300));
      setLoadedStates((prev) => ({ ...prev, balloon5: true }));

      // Balloon 6
      await new Promise((resolve) => setTimeout(resolve, 300));
      setLoadedStates((prev) => ({ ...prev, balloon6: true }));
    };

    loadSequence();
  }, []);

  const impacts = [
    {
      id: 1,
      title: "150K+ Green Jobs",
      description: "Creating sustainable employment",
      icon: Groups,
      color: "#4caf50",
      position: { top: "0%", left: "11%" },
      delay: 0,
      boxLoaded: loadedStates.balloon1,
    },
    {
      id: 2,
      title: "Green Industry Ecosystem",
      description: "Fueling economic growth through sustainability",
      icon: Nature,
      color: "#66bb6a",
      position: { top: "40%", left: "2%" },
      delay: 1,
      boxLoaded: loadedStates.balloon2,
    },
    {
      id: 3,
      title: "Community Mobilization",
      description: "Uniting citizens for greener Punjab",
      icon: Diversity3,
      color: "#4caf50",
      position: { top: "0%", left: "31%" },
      delay: 2,
      boxLoaded: loadedStates.balloon3,
    },
    {
      id: 4,
      title: "Waste-to-Value Transformation",
      description: "Powering energy through waste conversion",
      icon: Bolt,
      color: "#66bb6a",
      position: { top: "43%", right: "2.7%" },
      delay: 3,
      boxLoaded: loadedStates.balloon4,
    },
    {
      id: 5,
      title: "Circular Economy",
      description: "Ensuring every resource is reborn",
      icon: Autorenew,
      color: "#4caf50",
      position: { top: "0%", right: "10%" },
      delay: 1.5,
      boxLoaded: loadedStates.balloon5,
    },
    {
      id: 6,
      title: "Climate Resilience",
      description: "Turning challenges into opportunities",
      icon: Public,
      color: "#66bb6a",
      position: { top: "1%", right: "30%" },
      delay: 2.5,
      boxLoaded: loadedStates.balloon6,
    },
  ];

  return (
    <ModalContainer>
      {/* Centered Circle Header */}
      <CenterCircle>
        <MainTitle>♻️ Impact of Suthra Punjab</MainTitle>
        <Subtitle>6 Transformative Areas Driving Sustainable Change</Subtitle>
      </CenterCircle>

      {/* Impact Balloons */}
      <BalloonsContainer>
        {impacts.map((impact) => {
          const Icon = impact.icon;
          const isHovered = hoveredBalloon === impact.id;

          return (
            <ImpactBalloon
              key={impact.id}
              position={impact.position}
              delay={impact.delay}
              color={impact.color}
              hovered={isHovered}
              boxLoaded={impact.boxLoaded}
              onMouseEnter={() => setHoveredBalloon(impact.id)}
              onMouseLeave={() => setHoveredBalloon(null)}
            >
              <BalloonContent>
                <IconContainer
                  hovered={isHovered}
                  color={impact.color}
                  boxLoaded={impact.boxLoaded}
                >
                  <Icon fontSize="inherit" />
                </IconContainer>

                <BalloonTitle boxLoaded={impact.boxLoaded}>
                  {impact.title}
                </BalloonTitle>

                <BalloonDescription boxLoaded={impact.boxLoaded}>
                  {impact.description}
                </BalloonDescription>
              </BalloonContent>
            </ImpactBalloon>
          );
        })}
      </BalloonsContainer>

      {/* Video at the bottom */}
      <VideoContainer>
        <video
          controls
          autoPlay
          // muted
          loop
          src="videos/SuthraPunjab.mp4"
          preload="metadata"
          onEnded={handleVideoEnd}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </VideoContainer>
    </ModalContainer>
  );
};

export default Impact;
